import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, TouchableHighlight, TVEventHandler, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { AlertDetailsView, TalkbackOverrideView, TWCPager } from '@components';
import { useRecoilState } from 'recoil';

import { assets } from '@assets';
import { alertDetailsPageTestIDs } from '@constants';
import { helpers } from '@helpers';
import { useContextNodeHandle, useContextReference, useGetExpireTimeText } from '@hooks';
import { screenReaderEnabledState } from '@store';

import { tPath } from './constants';
import { styles } from './styles';

import { PageIndicator } from '../PageIndicator/PageIndicator';

export const AlertDetailsPageContent = ({ alertDetails, initialIndex = 0 }) => {
  const { isAndroid, isWeb, TimeUtils } = helpers;

  const [focusIndex, setFocusIndex] = useState(initialIndex);
  const pagerReference = useRef();

  const { t } = useTranslation();

  const [screenReaderEnabled] = useRecoilState(screenReaderEnabledState);

  const { eventDescription, expireTimeUTC, expireTimeLocalTimeZone, texts } = alertDetails[focusIndex];

  const headerLabel = eventDescription ?? '';
  const textLabel = texts ? `${texts[0].description.replace(/&&/g, '')}` : '';

  const isTodayTomorrowOrLater = TimeUtils.isTodayTomorrowOrLater(expireTimeUTC);

  const expireTimeLabel = useGetExpireTimeText({
    expireTimeUTC,
    issueTimeLocalTimeZone: expireTimeLocalTimeZone,
    isTodayTomorrowOrLater,
    tPath,
  });

  const accessibilityLabelText =
    alertDetails.length === 1
      ? t(`${tPath}accessibilityLabelSingle`, {
          headline: headerLabel,
          expire: expireTimeLabel,
          body: textLabel,
        })
      : t(`${tPath}accessibilityLabelMultiple`, {
          headline: headerLabel,
          expire: expireTimeLabel,
          body: textLabel,
          index: focusIndex + 1,
          count: alertDetails.length,
        });

  const accessibilityLabelHint =
    alertDetails.length - 1 === focusIndex
      ? t(`${tPath}accessibilityHintSingleTile`)
      : t(`${tPath}accessibilityHintMultiTiles`);

  const pagingScrollHandler = keyEvent => {
    if (alertDetails && alertDetails.length > 0) {
      const key = keyEvent.key;

      switch (key) {
        case 'ArrowLeft': {
          if (focusIndex > 0) {
            if (isWeb) {
              pagerReference.current?.slickGoTo(focusIndex - 1);
            } else {
              pagerReference.current?.setPage(focusIndex - 1);
            }
            keyEvent.preventDefault && keyEvent.preventDefault();
          }
          break;
        }
        case 'ArrowRight': {
          if (focusIndex < alertDetails.length - 1) {
            if (isWeb) {
              pagerReference.current?.slickGoTo(focusIndex + 1);
            } else {
              pagerReference.current?.setPage(focusIndex + 1);
            }
            keyEvent.preventDefault && keyEvent.preventDefault();
          }
          break;
        }
      }
    }
  };

  const isPageFocused = useIsFocused();

  useEffect(() => {
    const tvEventHandler = new TVEventHandler();

    // Don't need handlers if we don't have focus (some modals can appear on top of this one)
    if (isPageFocused) {
      if (isWeb) {
        document.addEventListener('keydown', pagingScrollHandler, true);
      } else {
        const handleEvent = (cmp, event) => {
          if (
            event &&
            event.eventType &&
            (event.eventType === 'left' || event.eventType === 'right') &&
            event.eventKeyAction === 0
          ) {
            pagingScrollHandler({
              key: event.eventType === 'left' ? 'ArrowLeft' : 'ArrowRight',
            });
          }
        };

        tvEventHandler.enable(null, handleEvent);
      }
    }
    return () => {
      if (isWeb) {
        document.removeEventListener('keydown', pagingScrollHandler, true);
      } else {
        tvEventHandler?.disable();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusIndex, alertDetails, pagerReference.current, isPageFocused]);

  const focusReferenceHandle = useContextNodeHandle(alertDetailsPageTestIDs.alertDetailsFocus);
  const focusTrap = isWeb ? null : focusReferenceHandle;

  const focusReference = useContextReference(alertDetailsPageTestIDs.alertDetailsFocus);

  return (
    <View style={styles.alertModalWrapper}>
      {alertDetails && alertDetails.length > 0 ? (
        <>
          <TWCPager
            initialIndex={initialIndex}
            ref={pagerReference}
            style={styles.pager}
            onIndexChanged={index => setFocusIndex(index)}
          >
            {alertDetails.map((alert, index) => (
              <View key={index} collapsable={false} style={styles.detailsViewWrapper}>
                <AlertDetailsView alertDetail={alert} index={index} isFocused={index === focusIndex} />
              </View>
            ))}
          </TWCPager>
          {alertDetails.length > 1 ? (
            <>
              <Image
                source={assets.arrowLeft}
                style={[styles.arrowLeft, focusIndex === 0 ? styles.arrowDisabled : styles.arrowEnabled]}
              />
              <Image
                source={assets.arrowRight}
                style={[
                  styles.arrowRight,
                  focusIndex === alertDetails.length - 1 ? styles.arrowDisabled : styles.arrowEnabled,
                ]}
              />
              <PageIndicator focusIndex={focusIndex} numberOfPages={alertDetails.length} />
            </>
          ) : null}
        </>
      ) : null}
      {isAndroid && screenReaderEnabled ? (
        <View style={styles.alertModalItem}>
          <TalkbackOverrideView
            accessibilityHint={accessibilityLabelHint}
            accessibilityLabel={accessibilityLabelText}
            height={1080}
            nativeID={alertDetailsPageTestIDs.alertDetailsFocus}
            ref={focusReference}
            talkbackRect={{
              x: 380,
              y: 60,
              width: 1160,
              height: 912,
            }}
            testID={alertDetailsPageTestIDs.alertDetailsFocus}
            width={1920}
          />
        </View>
      ) : (
        <TouchableHighlight
          accessibilityHint={accessibilityLabelHint}
          accessibilityLabel={accessibilityLabelText}
          accessibilityLiveRegion={'assertive'}
          accessible
          hasTVPreferredFocus
          nativeID={alertDetailsPageTestIDs.alertDetailsFocus}
          nextFocusDown={focusTrap}
          nextFocusLeft={focusTrap}
          nextFocusRight={focusTrap}
          nextFocusUp={focusTrap}
          ref={focusReference}
          style={styles.focusTrap}
          testID={alertDetailsPageTestIDs.alertDetailsFocus}
        >
          <View />
        </TouchableHighlight>
      )}
    </View>
  );
};
